import { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../api';

// Renders on the Student Dashboard. Shows a paid-renewal CTA when the
// authenticated student's CMA certificate is due_soon or expired, and
// finalizes the renewal after the student returns from Stripe with
// ?cma_renewed=1&session_id=...
const CMARenewalBanner = () => {
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const loadStatus = async () => {
    try {
      const res = await api.get('/online-courses/cma-renew/my-status');
      setStatus(res.data || null);
    } catch (_e) {
      setStatus(null);
    }
  };

  // On mount: load status, and if returning from a successful Stripe checkout,
  // finalize the renewal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const renewed = params.get('cma_renewed');
      if (sessionId && renewed === '1') {
        try {
          const res = await api.post('/online-courses/cma-renew/process-payment', { session_id: sessionId });
          if (!cancelled) setSuccessMsg(res.data?.message || 'Renewal complete.');
        } catch (err) {
          if (!cancelled) setSuccessMsg(err.response?.data?.detail || 'Could not finalize renewal.');
        } finally {
          // Strip checkout query params from the URL
          const url = new URL(window.location.href);
          url.searchParams.delete('session_id');
          url.searchParams.delete('cma_renewed');
          window.history.replaceState({}, '', url.toString());
        }
      }
      await loadStatus();
    })();
    return () => { cancelled = true; };
  }, []);

  const startCheckout = async () => {
    setWorking(true);
    try {
      const base = window.location.origin + window.location.pathname;
      const res = await api.post('/online-courses/cma-renew/checkout', {
        success_url: base,
        cancel_url: base,
      });
      window.location.assign(res.data.checkout_url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not start renewal checkout.');
      setWorking(false);
    }
  };

  if (successMsg) {
    return (
      <div
        data-testid="cma-renewal-success-banner"
        style={{
          background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)',
          border: '1px solid #16a34a',
          color: '#15803d',
          padding: 16,
          borderRadius: 12,
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <CheckCircle size={22} />
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>CMA Recertification Complete</div>
          <div style={{ fontSize: '0.875rem', marginTop: 2 }}>{successMsg}</div>
        </div>
      </div>
    );
  }

  if (!status || !status.has_cma_certificate) return null;
  if (status.status === 'current') return null;

  const isExpired = status.status === 'expired';
  const days = status.days_until_renewal;
  const expiresFmt = (() => {
    try { return new Date(status.renewal_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
    catch (_e) { return status.renewal_expires_at; }
  })();

  return (
    <div
      data-testid="cma-renewal-banner"
      style={{
        background: isExpired
          ? 'linear-gradient(135deg,#fee2e2,#fecaca)'
          : 'linear-gradient(135deg,#fef3c7,#fde68a)',
        border: `1px solid ${isExpired ? '#b91c1c' : '#b45309'}`,
        color: isExpired ? '#7f1d1d' : '#78350f',
        padding: 18,
        borderRadius: 12,
        marginBottom: 18,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <AlertTriangle size={28} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>
          {isExpired
            ? 'Your CMA certification has expired'
            : `Your CMA certification expires in ${days} day${days === 1 ? '' : 's'}`}
        </div>
        <div style={{ fontSize: '0.9rem', marginTop: 4 }}>
          {isExpired
            ? `It lapsed on ${expiresFmt}. Renew today to restore active credential status with a 2-year validity window.`
            : `Valid through ${expiresFmt}. Recertify now for a fresh 2-year cycle — no exam required.`}
        </div>
      </div>
      <button
        type="button"
        onClick={startCheckout}
        disabled={working}
        data-testid="cma-renewal-checkout-btn"
        style={{
          background: 'linear-gradient(135deg,#0d9488,#065f46)',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          padding: '12px 20px',
          fontSize: '0.95rem',
          fontWeight: 800,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          whiteSpace: 'nowrap',
        }}
      >
        <RefreshCw size={16} />
        {working ? 'Redirecting…' : `Renew for $${status.price_usd}`}
      </button>
    </div>
  );
};

export default CMARenewalBanner;
